import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Inbox, RefreshCw, Search, Wallet } from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { ReviewCard } from "../components/claims/ClaimCard";

/**
 * The review queue — one question only: is this a real expense?
 *
 * Paying lives on its own page, done by a different person. Keeping them apart
 * in the interface makes that distinction obvious, rather than a matter of
 * noticing which tab you happen to be on.
 */

const FILTERS = [
  ["open", "To review"],
  ["approved", "Approved"],
  ["history", "History"],
];

export default function ClaimsReviewPage() {
  const navigate = useNavigate();
  const { authAxios, token, API, isAdmin, isSubAdmin } = useAuth();
  const canPay = isAdmin || isSubAdmin;

  const [claims, setClaims] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("open");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const api = authAxios();
      const res = await api.get("/claims");
      setClaims(res.data.claims);
      setCounts(res.data.counts);
    } catch {
      toast.error("Could not load claims");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const decide = async (claim, body) => {
    try {
      const api = authAxios();
      await api.put(`/claims/${claim.claim_id}/decision`, body);
      toast.success(`Claim ${body.decision}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save the decision");
    }
  };

  const visible = claims.filter((c) => {
    if (filter === "open" && !["submitted", "queried"].includes(c.status)) return false;
    if (filter === "approved" && c.status !== "approved") return false;
    if (filter === "history" && !["paid", "rejected"].includes(c.status)) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [c.claimant_name, c.claimant_email, c.description, c.event_title, c.reference]
      .some((v) => (v || "").toLowerCase().includes(q));
  });

  if (loading) {
    return (
      <div className="p-4 md:p-8 w-full space-y-3">
        <div className="h-20 bg-muted rounded-xl animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  const countFor = (key) => ({
    open: (counts.submitted || 0) + (counts.queried || 0),
    approved: counts.approved || 0,
    history: (counts.paid || 0) + (counts.rejected || 0),
  }[key]);

  return (
    <div className="p-4 md:p-8 w-full space-y-5 pb-24">
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="sm" className="-ml-2 gap-1.5"
                onClick={() => navigate("/claims")} data-testid="review-back">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Overview
        </Button>
        <div className="flex items-center gap-2">
          {canPay && (
            <Button variant="outline" size="sm" className="gap-1.5"
                    onClick={() => navigate("/claims/payments")}
                    data-testid="review-goto-payments">
              <Wallet className="h-4 w-4" aria-hidden="true" />
              Payment run
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={load} aria-label="Refresh claims"
                  data-testid="review-refresh">
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>

      <h1 className="text-xl font-bold text-foreground">Review claims</h1>

      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Claim filters">
        {FILTERS.map(([key, label]) => (
          <Button key={key} role="tab" aria-selected={filter === key}
                  variant={filter === key ? "default" : "outline"} size="sm" className="h-10"
                  onClick={() => setFilter(key)} data-testid={`filter-${key}`}>
            {label}{countFor(key) ? ` (${countFor(key)})` : ""}
          </Button>
        ))}
      </div>

      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true" />
        <Input className="h-11 pl-9" placeholder="Search name, event or reference"
               aria-label="Search claims" value={query}
               onChange={(e) => setQuery(e.target.value)} data-testid="review-search" />
      </div>

      {visible.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center space-y-2" data-testid="review-empty">
            <Inbox className="h-8 w-8 mx-auto text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">
              {filter === "open" ? "Nothing waiting for review." : "Nothing here yet."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {visible.map((claim) => (
            <ReviewCard key={claim.claim_id} claim={claim} token={token}
                        api={API} onDecide={decide} />
          ))}
        </div>
      )}
    </div>
  );
}
